import React, { useState, useRef, useEffect } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router';
import styled from 'styled-components';

interface INavigationLinkProps {
  href: string;
  title: string;
  tooltip?: string;
  exact?: boolean;
}

const StyledNavigationLink = styled(NavLink)`
  min-width: 100px;
  padding: 15px;
  font-size: 0.9rem;
  text-align: center;
  text-decoration: none;
  color: rgba(255, 255, 255, 0.45);
  background-color: rgba(255, 255, 255, 0.08);

  &.active,
  &:hover {
    color: rgba(255, 255, 255, 1);
  }
`;

const AddLink = styled(StyledNavigationLink)`
  min-width: auto;
  box-sizing: content-box;
  padding: 15px 0px;
  width: 50px;
  font-weight: bold;
  color: rgba(255, 255, 255, 1);
  background-color: rgba(92, 184, 92, 1);

  &:hover {
    color: rgba(0, 0, 0, 0.35);
  }
`;

const HomeLink = styled(StyledNavigationLink)`
  min-width: auto;
  box-sizing: content-box;
  padding: 15px 0;
  width: 50px;
  margin-right: 1px;

  &:hover {
    text-shadow: 0px 0px 2px cyan;
  }
`;

export const NavigationHomeLink: React.FC<INavigationLinkProps> = (props: INavigationLinkProps) => {
  const { href, tooltip } = props;
  return (
    <HomeLink end to={href} title={tooltip}>
      {props.title}
    </HomeLink>
  );
};

interface LocationState {
  returnSearch?: string;
}

export const NavigationAddLink: React.FC<INavigationLinkProps> = (props: INavigationLinkProps) => {
  const { href, tooltip } = props;
  const location = useLocation();

  // When navigating to create a new entry from bookkeeping pages,
  // preserve the current search params so they can be restored when returning.
  // Check both:
  // 1. location.search - when on list page with URL filters
  // 2. location.state.returnSearch - when in editor (filters stored in state)
  const isOnBookkeepingPage = location.pathname.startsWith('/bookkeeping');
  const existingReturnSearch = (location.state as LocationState)?.returnSearch;
  const searchToPreserve = location.search || existingReturnSearch;
  const state = isOnBookkeepingPage && searchToPreserve ? { returnSearch: searchToPreserve } : undefined;

  return (
    <AddLink end to={href} state={state} title={tooltip}>
      {props.title}
    </AddLink>
  );
};

export const NavigationLink: React.FC<INavigationLinkProps> = (props: INavigationLinkProps) => {
  const { exact, href, tooltip } = props;
  // In React Router v6, 'exact' is replaced with 'end'
  // end={true} is the default (matches exactly), end={false} matches path prefix
  return (
    <StyledNavigationLink end={exact !== false} to={href} title={tooltip}>
      {props.title}
    </StyledNavigationLink>
  );
};

// ============================================================================
// Navigation Add Dropdown
// ============================================================================

const DropdownContainer = styled.div`
  position: relative;
  display: inline-block;
`;

const AddButton = styled.button`
  min-width: auto;
  box-sizing: content-box;
  padding: 15px 0px;
  width: 50px;
  font-weight: bold;
  font-size: 0.9rem;
  color: rgba(255, 255, 255, 1);
  background-color: rgba(92, 184, 92, 1);
  border: none;
  cursor: pointer;

  &:hover {
    color: rgba(0, 0, 0, 0.35);
  }
`;

const DropdownMenu = styled.div<{ $isOpen: boolean }>`
  display: ${(props) => (props.$isOpen ? 'block' : 'none')};
  position: absolute;
  top: 100%;
  right: 0;
  min-width: 180px;
  background-color: #fff;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  border-radius: 4px;
  z-index: 1000;
  overflow: hidden;
`;

const DropdownItem = styled.button`
  display: block;
  width: 100%;
  padding: 12px 16px;
  text-align: left;
  background: none;
  border: none;
  cursor: pointer;
  font-size: 0.9rem;
  color: #333;

  &:hover {
    background-color: #f5f5f5;
  }

  &:not(:last-child) {
    border-bottom: 1px solid #eee;
  }
`;

interface AddDropdownOption {
  label: string;
  href: string;
}

interface NavigationAddDropdownProps {
  title: string;
  tooltip?: string;
  options: AddDropdownOption[];
}

export const NavigationAddDropdown: React.FC<NavigationAddDropdownProps> = ({
  title,
  tooltip,
  options,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const location = useLocation();

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Close dropdown on Escape key
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
      return () => document.removeEventListener('keydown', handleKeyDown);
    }
  }, [isOpen]);

  const handleOptionClick = (href: string) => {
    setIsOpen(false);

    // Preserve search params when navigating from bookkeeping pages
    const isOnBookkeepingPage = location.pathname.startsWith('/bookkeeping');
    const existingReturnSearch = (location.state as LocationState)?.returnSearch;
    const searchToPreserve = location.search || existingReturnSearch;
    const state = isOnBookkeepingPage && searchToPreserve ? { returnSearch: searchToPreserve } : undefined;

    // For measurements page, merge current filters (tank/terminal) into the destination URL
    // This allows "New measurement" to open with the currently selected tank
    if (href.includes('/measurements') && searchToPreserve) {
      const currentParams = new URLSearchParams(searchToPreserve);
      const [basePath, destSearch] = href.split('?');
      const destParams = new URLSearchParams(destSearch || '');

      // Copy tank and terminal from current filters to destination
      const tank = currentParams.get('tank');
      const terminal = currentParams.get('terminal');
      if (tank) destParams.set('tank', tank);
      if (terminal) destParams.set('terminal', terminal);

      const finalUrl = `${basePath}?${destParams.toString()}`;
      navigate(finalUrl, { state });
    } else {
      navigate(href, { state });
    }
  };

  return (
    <DropdownContainer ref={dropdownRef}>
      <AddButton
        type="button"
        title={tooltip}
        onClick={() => setIsOpen(!isOpen)}
        aria-haspopup="true"
        aria-expanded={isOpen}
      >
        {title}
      </AddButton>
      <DropdownMenu $isOpen={isOpen}>
        {options.map((option) => (
          <DropdownItem
            key={option.href}
            type="button"
            onClick={() => handleOptionClick(option.href)}
          >
            {option.label}
          </DropdownItem>
        ))}
      </DropdownMenu>
    </DropdownContainer>
  );
};

export default NavigationLink;
